import React, { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils"; 
import { Loader2 } from "lucide-react"; 

const buttonVariants = cva(
  "relative inline-flex items-center justify-center overflow-hidden rounded-full px-6 py-2 font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 active:scale-95 cursor-pointer  hover:brightness-110 hover:scale-105 whitespace-nowrap shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:brightness-110 hover:scale-105",
        outline: "border border-gray-500 bg-background hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-10 px-6 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const RippleButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, isLoading, children, leftIcon, rightIcon, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={isLoading || disabled}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {/* Ripple Overlay */}
        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="h-full w-full animate-ripple rounded-full bg-white opacity-0 transition-opacity active:opacity-20" />
        </span>

        {/* Content handling */}
        <span className="relative z-10 flex items-center gap-2">
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {!isLoading && leftIcon && <span>{leftIcon}</span>}
          
          {children}
          
          {!isLoading && rightIcon && <span>{rightIcon}</span>}
        </span>
      </button>
    );
  }
);

RippleButton.displayName = "RippleButton";

export { RippleButton, buttonVariants };